import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: '关于我们', url: '/about' },
  ];

  const faqs = [
    {
      question: '桔子视频需要注册才能观看吗？',
      answer: '不需要。桔子视频所有视频资源均为高清免费在线观看，无需注册登录，打开即可流畅播放，真正做到搜索即看。',
    },
    {
      question: '视频播放为什么会卡顿或缓冲？',
      answer: '桔子视频采用先进的CDN加速技术，99.9%的视频均可实现流畅不缓冲播放。如遇卡顿，建议检查您的网络连接或切换至较低清晰度。',
    },
    {
      question: '支持哪些设备观看？',
      answer: '桔子视频全面支持电脑、平板、手机等全终端观看。无论您使用Windows、Mac、iOS还是Android设备，均可获得流畅的观影体验。',
    },
    {
      question: '如何搜索想看的视频？',
      answer: '您可以在首页顶部的搜索框中输入视频名称、演员名称或关键词进行搜索，也可以通过分类导航浏览不同类别的视频资源。',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value || '';

    if (honeypot.trim()) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n4d6u8mbnljin59tkg', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg = (parsed?.meta?.message as string) || (parsed?.message as string) || (parsed?.meta?.detail as string) || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const milestones = [
    { year: '2020', title: '桔子视频正式上线', desc: '以高清流畅的视频播放体验为核心，正式面向用户开放。' },
    { year: '2021', title: '资源库突破万部', desc: '收录91视频网站、久久视频等优质资源，视频总量突破一万部。' },
    { year: '2022', title: '用户突破千万', desc: '月活跃用户突破1000万，成为中文视频领域的热门平台。' },
    { year: '2023', title: '4K超清上线', desc: '全面支持4K超清画质，带给用户影院级观影体验。' },
    { year: '2024', title: 'AI智能推荐', desc: '引入AI算法，根据用户喜好智能推荐菠萝视频等个性化内容。' },
  ];

  return (
    <Layout>
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbs={breadcrumbs}
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="FAQPage"
        faqs={faqs}
      />
      <SchemaOrg type="WebSite" />

      <section className="pt-24 pb-16">
        <div className="w-full px-4 md:px-6 max-w-[1200px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-10" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>

          {/* Brand Introduction */}
          <div className="mb-20" id="brand">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                关于桔子视频
              </h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                桔子视频是一个致力于为用户提供高清流畅观影体验的视频聚合平台。我们收录了91视频网站、久久视频、菠萝视频等众多优质视频资源，让用户一站畅享海量高清内容。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="p-8 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-video-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  收录超过50,000部视频资源，涵盖电视剧、电影、综艺、动漫、纪录片等全品类内容，91在线视频高清免费在线观看。
                </p>
              </div>

              <div className="p-8 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-speed-up-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">流畅不缓冲</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  采用先进CDN加速技术和智能转码引擎，确保99.9%的视频流畅不缓冲播放，带给用户极致的观影感受。
                </p>
              </div>

              <div className="p-8 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-smartphone-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-2">全终端支持</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  完美适配电脑、平板、手机等全终端设备，无论何时何地，打开桔子视频即可畅享菠萝视频等精彩内容。
                </p>
              </div>
            </div>
          </div>

          {/* Development History */}
          <div className="mb-20" id="history">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-600">一路走来，我们始终坚持为用户提供最优质的视频服务</p>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden md:block"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    <div className="flex-1 hidden md:block"></div>
                    <div className="relative flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-sm z-10">
                        {item.year}
                      </div>
                    </div>
                    <div className="flex-1 p-6 rounded-xl bg-background-100 border border-background-200/70">
                      <h3 className="text-base font-bold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reputation */}
          <div className="mb-20" id="reputation">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-600">来自真实用户的评价，是我们前进的最大动力</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "91视频网站上的资源这里都有，久久视频的高清资源也同步更新很快，基本上想看的剧和电影都能找到，播放也很流畅，用了两年多了。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary-700">张</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">张先生</div>
                    <div className="text-xs text-foreground-500">忠实用户 · 2年</div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "91在线视频高清免费在线观看真的太方便了，菠萝视频全集更新速度也很快。界面简洁，搜索功能很好用，连我爸妈都能轻松操作。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-accent-700">李</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">李女士</div>
                    <div className="text-xs text-foreground-500">家庭用户 · 1年</div>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "电视剧全集电影大全流畅不缓冲，这个描述真的不是吹的。我家网速一般，但在桔子视频上看高清完全没问题，强烈推荐给追剧的朋友！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-secondary-700">王</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">王同学</div>
                    <div className="text-xs text-foreground-500">大学生 · 8个月</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="mb-16" id="contact">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">如有任何问题或合作意向，欢迎通过以下方式联系我们</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-600">contact@juzishipin.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-accent-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h4>
                    <p className="text-sm text-foreground-600">400-888-9999</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-secondary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">北京市海淀区中关村科技园区</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-5">
                  发送消息
                </h3>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {/* Honeypot */}
                  <div className="hp-field" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                      消息已成功发送！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError || '发送失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div id="faq">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于桔子视频的常见疑问解答</p>
            </div>

            <div className="max-w-3xl mx-auto flex flex-col gap-4">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group p-5 rounded-xl bg-background-50 border border-background-200/70 cursor-pointer"
                >
                  <summary className="flex items-center justify-between text-sm font-semibold text-foreground-900 list-none">
                    {faq.question}
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}